import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  KeyRound, Copy, Loader2, RotateCw, ShieldCheck, AlertTriangle, Eye, EyeOff,
  CheckCircle2, XCircle, Clock, Lock, Zap, FileText, BookOpen, Hash, Calendar,
  Trash2,
} from 'lucide-react';
import { Card } from '../components/Card';
import { Stat, SectionHeader } from '../components/Tabs';
import { Badge, Dot } from '../components/Badge';
import { Button } from '../components/Button';
import { Dialog, EmptyState, Spinner } from '../components/Dialog';
import { apiFetch } from '../api';
import { supabase } from '../supabaseClient';
import { formatNumber, formatRelative, copyToClipboard } from '../lib/utils';

/**
 * KeysPage — Phase 3 update.
 *
 * Invariant: every customer has exactly ONE active API key.
 *
 * Layout (top to bottom):
 *   1. SectionHeader with "Regenerate key" button
 *   2. Stat row (Active / Rate limit / Credits per call)
 *   3. Active Key card — full plaintext + copy + secret UX
 *   4. Security guidance card (3 rules)
 *   5. Usage tips card (where to put the key in headers)
 *
 * Custom dialogs (no browser confirm()):
 *   - <RegenerateConfirmDialog/> — destructive, lists what will happen
 *   - <RevealKeyDialog/>         — shows plaintext once after regen
 */
export default function KeysPage({ billing }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Per-key state in memory (cleared on reload — plaintext never persisted client-side)
  // Map<keyId, { plaintext: string, issuedAt: Date }>
  const [knownPlaintexts, setKnownPlaintexts] = useState(new Map());
  const [revealOpen, setRevealOpen] = useState(null); // { keyId, plaintext, reason }
  const [confirmRegenOpen, setConfirmRegenOpen] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);

  async function load() {
    setLoading(true); setError(null);
    try {
      const data = await apiFetch('/v1/keys');
      setKeys(data.keys || []);
      // Phase-9: auto-fetch active plaintext
      try {
        const { data: sess } = await supabase.auth.getSession();
        const jwt = sess?.session?.access_token;
        if (jwt) {
          const r = await apiFetch('/v1/keys/active-plaintext', {
            headers: { 'x-user-token': jwt },
          });
          if (r.ok) {
            const kd = await r.json();
            if (kd?.plaintext_key && kd?.key_id) {
              setKnownPlaintexts((prev) => {
                const next = new Map(prev);
                next.set(kd.key_id, { plaintext: kd.plaintext_key, issuedAt: new Date() });
                return next;
              });
            }
          }
        }
      } catch (e) {}
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }
  // Fetch the one-shot pending delivery on mount. Returns 204 if none.
  const checkPending = useCallback(async () => {
    try {
      const r = await apiFetch('/v1/keys/pending', {
        headers: {
          'x-user-token': getJwtFromStorage(),
          'Content-Type': 'application/json',
        },
      });
      if (r.status === 200) {
        const data = await r.json();
        setRevealOpen({
          keyId: null, // not yet known
          plaintext: data.plaintext_key,
          reason: 'first-signin',
        });
      }
    } catch {
      // 204 or network error — fine
    }
  }, []);

  // Only run the pending check once per session (sessionStorage).
  // Otherwise the dialog re-pops every time the user navigates back.
  useEffect(() => {
    if (sessionStorage.getItem('keys.pending.shown')) return;
    sessionStorage.setItem('keys.pending.shown', '1');
    checkPending();
  }, [checkPending]);

  useEffect(() => { load(); }, []);

  // When reveal is dismissed, save the plaintext to memory keyed by the
  // (about-to-be-known) active key id.
  function handleRevealClose(savedKeyId) {
    if (revealOpen && savedKeyId) {
      setKnownPlaintexts((prev) => {
        const next = new Map(prev);
        next.set(savedKeyId, {
          plaintext: revealOpen.plaintext,
          issuedAt: new Date(),
        });
        return next;
      });
    }
    setRevealOpen(null);
  }

  async function doRegenerate() {
    setRegenBusy(true);
    try {
      const data = await apiFetch('/v1/keys/regenerate', { method: 'POST' });
      await load(); // refresh list so we know the new key id
      setRevealOpen({
        keyId: data.id,
        plaintext: data.plaintext_key,
        reason: 'regenerated',
      });
    } catch (e) {
      setError(`Regenerate failed: ${e.message || e}`);
    } finally {
      setRegenBusy(false);
      setConfirmRegenOpen(false);
    }
  }

  // Only show ACTIVE keys. Revoked keys are hidden per spec.
  const activeKey = useMemo(() => keys.find((k) => k.active), [keys]);
  const rpm = billing?.rate_limit_per_minute ?? 60;
  const activePlaintext = activeKey ? knownPlaintexts.get(activeKey.id)?.plaintext : null;

  return (
    <div className="space-y-8">
      <SectionHeader
        title="API keys"
        description="Every account has one active key. Regenerate to rotate — your old key stops working immediately."
        actions={
          <Button variant="primary" onClick={() => setConfirmRegenOpen(true)} disabled={regenBusy}>
            <RotateCw className={cn('h-4 w-4', regenBusy && 'animate-spin')} />
            Regenerate key
          </Button>
        }
      />

      {/* Stat row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Active keys" value="1" icon={KeyRound} hint="One per account" />
        <Stat label="Concurrency" value={`${rpm}`} icon={ShieldCheck} hint="Max simultaneous requests" />
        <Stat
          label="Credits per call"
          value="1"
          icon={Zap}
          hint="Same regardless of plan"
        />
      </div>

      {/* Active key — hero card */}
      <ActiveKeyCard
        activeKey={activeKey}
        plaintext={activePlaintext}
        loading={loading}
        error={error}
        onRetry={load}
        onRevealAgain={() => activePlaintext && setRevealOpen({
          keyId: activeKey.id,
          plaintext: activePlaintext,
          reason: 'reveal',
        })}
      />

      {/* Security guidance */}
      <SecurityTipsCard />

      {/* Usage tips */}
      <UsageTipsCard apiKeyPrefix={activeKey?.key_prefix} />

      {/* Confirm + reveal dialogs */}
      <RegenerateConfirmDialog
        open={confirmRegenOpen}
        onClose={() => setConfirmRegenOpen(false)}
        onConfirm={doRegenerate}
        busy={regenBusy}
      />
      <RevealKeyDialog
        keyData={revealOpen}
        activeKeyId={activeKey?.id}
        onClose={handleRevealClose}
      />
    </div>
  );
}

// =============================================================================
// Active Key Card — the centerpiece
// =============================================================================
function ActiveKeyCard({ activeKey, plaintext, loading, error, onRetry, onRevealAgain }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedFull, setCopiedFull] = useState(false);

  // Mask the prefix display (always show prefix even if no plaintext yet)
  const prefix = activeKey?.key_prefix || 'sk_••••••';
  const hasFullPlaintext = !!plaintext;

  async function copyPrefix() {
    if (!activeKey?.key_prefix) return;
    const ok = await copyToClipboard(activeKey.key_prefix);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  async function copyFull() {
    if (!plaintext) return;
    const ok = await copyToClipboard(plaintext);
    if (ok) {
      setCopiedFull(true);
      setTimeout(() => setCopiedFull(false), 1500);
    }
  }

  if (loading) {
    return (
      <Card>
        <Card.Body className="p-8 flex items-center justify-center">
          <Spinner size={20} />
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Body className="p-8">
          <EmptyState
            icon={AlertTriangle}
            title="Couldn't load keys"
            description={error}
            action={<Button variant="secondary" size="sm" onClick={onRetry}>Try again</Button>}
          />
        </Card.Body>
      </Card>
    );
  }

  if (!activeKey) {
    return (
      <Card>
        <Card.Body className="p-8">
          <EmptyState
            icon={KeyRound}
            title="No active key yet"
            description="Your key is being provisioned. Refresh in a moment."
            action={<Button variant="primary" size="sm" onClick={onRetry}>Refresh</Button>}
          />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-brand-softest border border-brand/30 flex items-center justify-center shrink-0">
              <KeyRound className="h-5 w-5 text-brand" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-fg">Active API key</h3>
                <Badge variant="success">
                  <Dot variant="success" />
                  Active
                </Badge>
                {hasFullPlaintext && (
                  <Badge variant="brand">Just issued</Badge>
                )}
              </div>
              <p className="text-xs text-fg-muted mt-0.5">
                Use this key in the <code className="font-mono text-fg">x-api-key</code> header of every API call.
              </p>
            </div>
          </div>
        </div>

        {/* Key display */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-2xs font-medium text-fg-muted uppercase tracking-wider">
              Key
            </label>
            {hasFullPlaintext && (
              <button
                type="button"
                onClick={() => setRevealed((v) => !v)}
                className="inline-flex items-center gap-1 text-2xs text-fg-muted hover:text-fg transition-colors"
              >
                {revealed
                  ? <><EyeOff className="h-3 w-3" /> Hide</>
                  : <><Eye className="h-3 w-3" /> Reveal</>}
              </button>
            )}
          </div>

          {/* Full plaintext (when known + revealed) OR prefix only */}
          <div className="relative rounded-md bg-surface-2 border border-border p-3 font-mono text-sm">
            <code className="block break-all text-fg">
              {hasFullPlaintext && revealed ? plaintext : maskKey(activeKey.key_prefix)}
            </code>
          </div>

          {/* Actions row */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {hasFullPlaintext ? (
              <>
                <Button variant="secondary" size="md" onClick={copyFull}>
                  {copiedFull
                    ? <><ShieldCheck className="h-3.5 w-3.5 text-success" /> Copied!</>
                    : <><Copy className="h-3.5 w-3.5" /> Copy full key</>}
                </Button>
                <Button variant="ghost" size="md" onClick={copyPrefix}>
                  {copied
                    ? <><ShieldCheck className="h-3.5 w-3.5 text-success" /> Copied</>
                    : <><Copy className="h-3.5 w-3.5" /> Copy prefix</>}
                </Button>
                <Button variant="ghost" size="md" onClick={onRevealAgain}>
                  <Eye className="h-3.5 w-3.5" /> Show again
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" size="md" onClick={copyPrefix}>
                  {copied
                    ? <><ShieldCheck className="h-3.5 w-3.5 text-success" /> Copied</>
                    : <><Copy className="h-3.5 w-3.5" /> Copy prefix</>}
                </Button>
                <span className="text-xs text-fg-subtle">
                  Full key shown only at creation — regenerate to see it again
                </span>
              </>
            )}
          </div>
        </div>

        {/* Metadata strip */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-border">
          <Meta icon={Hash} label="Prefix" value={activeKey.key_prefix} />
          <Meta icon={Clock} label="Last used" value={activeKey.last_used_at ? formatRelative(activeKey.last_used_at) : 'never'} />
          <Meta icon={Calendar} label="Created" value={activeKey.created_at ? formatRelative(activeKey.created_at) : '—'} />
          <Meta icon={ShieldCheck} label="Concurrency" value={activeKey.rate_limit_per_minute ?? '—'} hint="Max simultaneous requests" />
        </div>
      </Card.Body>
    </Card>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-2xs uppercase tracking-wider text-fg-muted">
        <Icon className="h-3 w-3" />
        <span>{label}</span>
      </div>
      <div className="text-sm font-medium text-fg mt-1 tabular-nums">{value}</div>
    </div>
  );
}

// =============================================================================
// Security Tips Card
// =============================================================================
function SecurityTipsCard() {
  return (
    <Card>
      <Card.Body className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="h-4 w-4 text-fg-muted" />
          <h3 className="text-sm font-semibold text-fg">Keep your key safe</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Tip
            icon={XCircle}
            tone="danger"
            title="Never expose in the browser"
            body="Don't commit to git, hardcode in client-side JS, or paste in chat. Treat it like a password."
          />
          <Tip
            icon={CheckCircle2}
            tone="success"
            title="Use environment variables"
            body="Store in a secret manager (AWS Secrets, Doppler, .env on the server). Rotate if it leaks."
          />
          <Tip
            icon={RotateCw}
            tone="brand"
            title="Regenerate if compromised"
            body="Hit regenerate above. Your old key stops working immediately — update your server within minutes."
          />
        </div>
      </Card.Body>
    </Card>
  );
}

function Tip({ icon: Icon, tone, title, body }) {
  const toneClasses = {
    danger: 'bg-destructive-soft text-destructive',
    success: 'bg-success-soft text-success-fg',
    brand: 'bg-brand-softest text-brand',
  };
  return (
    <div className="rounded-lg border border-border bg-surface-2/40 p-4">
      <div className={cn('h-8 w-8 rounded-md flex items-center justify-center mb-2', toneClasses[tone])}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-xs font-semibold text-fg mb-1">{title}</div>
      <p className="text-2xs text-fg-muted leading-relaxed">{body}</p>
    </div>
  );
}

// =============================================================================
// Usage Tips Card — how to use the key
// =============================================================================
function UsageTipsCard({ apiKeyPrefix }) {
  const prefix = apiKeyPrefix || 'sk_...';
  return (
    <Card>
      <Card.Body className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="h-4 w-4 text-fg-muted" />
          <h3 className="text-sm font-semibold text-fg">How to use your key</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* curl */}
          <CodeSample
            label="curl"
            icon={FileText}
            code={`curl -X POST https://api.datames.dev/v1/fetch \\
  -H "x-api-key: ${prefix}..." \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://example.com"}'`}
          />

          {/* python */}
          <CodeSample
            label="Python"
            icon={FileText}
            code={`import requests

r = requests.post(
    "https://api.datames.dev/v1/fetch",
    headers={"x-api-key": "${prefix}..."},
    json={"url": "https://example.com"},
)
print(r.json())`}
          />
        </div>

        <div className="mt-4 flex items-start gap-2 p-3 rounded-md bg-warning-soft border border-warning/20 text-xs text-fg">
          <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />
          <div>
            <strong className="font-semibold">Heads up:</strong> 1 credit is consumed per successful call,
            regardless of the plan. Failed / rejected requests don't bill.
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

function CodeSample({ label, icon: Icon, code }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    const ok = await copyToClipboard(code);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }
  return (
    <div className="rounded-lg border border-border bg-surface-2/40 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-surface-3/40">
        <div className="flex items-center gap-1.5 text-2xs font-medium text-fg-muted uppercase tracking-wider">
          <Icon className="h-3 w-3" />
          <span>{label}</span>
        </div>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 text-2xs text-fg-muted hover:text-fg transition-colors"
        >
          {copied
            ? <><ShieldCheck className="h-3 w-3 text-success" /> Copied</>
            : <><Copy className="h-3 w-3" /> Copy</>}
        </button>
      </div>
      <pre className="px-3 py-2.5 text-xs font-mono text-fg overflow-x-auto whitespace-pre">
{code}
      </pre>
    </div>
  );
}

// =============================================================================
// Custom Regenerate Confirmation Dialog (no browser confirm())
// =============================================================================
function RegenerateConfirmDialog({ open, onClose, onConfirm, busy }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Regenerate API key?"
      description="This is irreversible. Your existing key will stop working immediately."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={busy}>
            {busy ? <><Spinner size={14} /> Regenerating…</> : <><RotateCw className="h-3.5 w-3.5" /> Yes, regenerate</>}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Consequence
          icon={XCircle}
          tone="danger"
          title="Your current key stops working immediately"
          body="Any service using it will receive 401 Unauthorized until you update them with the new key."
        />
        <Consequence
          icon={RotateCw}
          tone="brand"
          title="A new key is issued and shown once"
          body="We'll display the new plaintext — copy it before closing. You can rotate again later if needed."
        />
        <Consequence
          icon={Trash2}
          tone="danger"
          title="Your old key is hard-deleted from the database"
          body="The previous hash, prefix, and metadata are gone. Nothing is retained — there are no audit rows for old keys."
        />
      </div>
    </Dialog>
  );
}

function Consequence({ icon: Icon, tone, title, body }) {
  const toneClasses = {
    danger: 'bg-destructive-soft text-destructive',
    brand: 'bg-brand-softest text-brand',
    success: 'bg-success-soft text-success-fg',
  };
  return (
    <div className="flex items-start gap-3 p-3 rounded-md border border-border bg-surface-2/40">
      <div className={cn('h-7 w-7 rounded-md flex items-center justify-center shrink-0', toneClasses[tone])}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold text-fg">{title}</div>
        <p className="text-2xs text-fg-muted mt-0.5 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

// =============================================================================
// Reveal Key Dialog (post-creation / post-regenerate)
// =============================================================================
function RevealKeyDialog({ keyData, activeKeyId, onClose }) {
  const [copied, setCopied] = useState(false);

  // Reset copy state when dialog opens fresh
  useEffect(() => { setCopied(false); }, [keyData]);

  if (!keyData) return null;
  const isFirstSignin = keyData.reason === 'first-signin';
  const isRegenerated = keyData.reason === 'regenerated';

  function handleClose() {
    // Pass activeKeyId so parent can stash plaintext in memory.
    // For first-signin we don't have an id yet — it's known after the
    // first /v1/keys fetch which the parent triggers.
    onClose?.(activeKeyId || null);
  }

  return (
    <Dialog
      open={!!keyData}
      onClose={handleClose}
      title={isFirstSignin ? 'Welcome — your API key' : isRegenerated ? 'Key regenerated' : 'Your API key'}
      description={
        isFirstSignin
          ? 'Your key was created automatically. Save it now — this is the only time we\'ll show the full key.'
          : isRegenerated
          ? 'Your previous key is now revoked. Update your server with the new one.'
          : 'The full key is shown below. Copy it before closing.'
      }
      size="lg"
      footer={
        <Button variant="primary" onClick={handleClose}>I've saved it</Button>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2 p-3 rounded-md bg-warning-soft border border-warning/30 text-xs text-fg">
          <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
          <span>This is the only time we'll show the full key. Save it somewhere safe.</span>
        </div>
        <div>
          <div className="text-2xs font-medium text-fg-muted uppercase tracking-wider mb-1.5">
            Full API key
          </div>
          <div className="flex gap-2">
            <code className="flex-1 mono-key font-mono text-sm break-all px-3 py-2 rounded-md bg-surface-2 border border-border">
              {keyData.plaintext}
            </code>
            <Button
              variant="secondary"
              size="md"
              onClick={async () => {
                const ok = await copyToClipboard(keyData.plaintext);
                if (ok) {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }
              }}
              className="shrink-0"
            >
              {copied
                ? <><ShieldCheck className="h-3.5 w-3.5 text-success" /> Copied</>
                : <><Copy className="h-3.5 w-3.5" /> Copy</>}
            </Button>
          </div>
        </div>
        <div className="text-xs text-fg-muted leading-relaxed">
          Paste it in your <code className="font-mono text-fg">x-api-key</code> header.
          You can always copy the prefix from the Active Key card on this page,
          and regenerate for a new full key at any time.
        </div>
      </div>
    </Dialog>
  );
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Mask a key prefix for display when the full plaintext isn't known:
 *   sk_AbCdEfGh → sk_AbCd••••
 */
function maskKey(prefix) {
  if (!prefix) return 'sk_••••••';
  if (prefix.length <= 4) return prefix + '•••';
  return prefix.slice(0, 4) + '••••••••••••';
}

/** Pull the Supabase JWT from localStorage for /v1/keys/pending. */
function getJwtFromStorage() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      const v = localStorage.getItem(k);
      if (k.includes('auth-token') && v) {
        try {
          const parsed = JSON.parse(v);
          if (parsed?.access_token) return parsed.access_token;
        } catch {}
      }
      if (v && v.startsWith('eyJ') && v.length > 100) return v;
    }
  } catch {}
  return '';
}

// cn utility inline (so this file is standalone-importable)
function cn(...args) {
  return args.flat().filter(Boolean).join(' ');
}